import React, { useState, useEffect, useRef } from 'react';
import { useVideoRecorder, VideoTake } from './useVideoRecorder';
import { useOrientation, RecordingCountdown } from '../shared';
import { setUnsavedCapture } from '../shared/unsavedWork';
import { AUDIO } from '../../../../utils/audioFeedback';
import { VideoReady } from './VideoReady';
import { VideoPreview } from './VideoPreview';
import { VideoReview } from './VideoReview';
import { VideoNotes } from './VideoNotes';
import type { InitialDraft } from '../shared';
import './VideoCapture.css';

export type VideoStep = 'viewfinder' | 'preview' | 'review' | 'notes';

export interface VideoCaptureProps {
  taskName: string;
  prompt: string;
  onSubmit: (videoBlobs: Blob[], notes: string) => void;
  onSaveDraft: (videoBlobs: Blob[], notes: string) => void;
  onClose: () => void;
  initialNotes?: string;
  initialDraft?: InitialDraft;
}

export const VideoCapture: React.FC<VideoCaptureProps> = ({
  taskName,
  prompt,
  onSubmit,
  onSaveDraft,
  onClose,
  initialNotes,
  initialDraft,
}) => {
  const hasDraftMedia = !!initialDraft?.mediaBlobs.length;
  const [step, setStep] = useState<VideoStep>(hasDraftMedia ? 'review' : 'viewfinder');
  const [showCountdown, setShowCountdown] = useState(false);
  const recorder = useVideoRecorder();
  const { isLandscape } = useOrientation();
  const injectedRef = useRef(false);
  const prevTakeCount = useRef(0);

  /* Recorded takes not yet saved → page-level leave nudge */
  useEffect(() => {
    setUnsavedCapture(recorder.takes.length > 0);
    return () => setUnsavedCapture(false);
  }, [recorder.takes.length]);

  /* Inject draft takes on mount */
  useEffect(() => {
    if (hasDraftMedia && !injectedRef.current) {
      injectedRef.current = true;
      const takes: VideoTake[] = initialDraft!.mediaBlobs.map((blob, i) => ({
        id: `draft-${i}`,
        blob,
        url: URL.createObjectURL(blob),
        duration: initialDraft!.mediaDurations?.[i] ?? 0,
        orientation: null,
      }));
      recorder.injectTakes(takes);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* If all takes deleted during review, go back to viewfinder */
  useEffect(() => {
    if (step === 'review' && recorder.takes.length === 0 && prevTakeCount.current > 0) {
      setStep('viewfinder');
    }
    prevTakeCount.current = recorder.takes.length;
  }, [step, recorder.takes.length]);

  const handleRecord = () => {
    AUDIO.tap();
    setShowCountdown(true);
  };

  const handleCountdownComplete = () => {
    setShowCountdown(false);
    AUDIO.start();
    recorder.startRecording();
  };

  const handleStop = () => {
    AUDIO.stop();
    recorder.stopRecording();
    setStep('preview');
  };

  const handleRetake = () => {
    recorder.reset();
    setStep('viewfinder');
  };

  const handleUseVideo = () => {
    recorder.acceptTake();
    recorder.stopCamera();
    setStep('review');
  };

  const handleDeleteTake = (takeId: string) => {
    recorder.deleteTake(takeId);
  };

  const handleRecordAnother = () => {
    // Takes are already saved — just go back to viewfinder for a new recording
    setStep('viewfinder');
  };

  const handleSubmit = (notes: string) => {
    if (recorder.takes.length > 0) {
      onSubmit(recorder.takes.map(t => t.blob), notes);
    }
  };

  const handleSaveDraft = (notes: string) => {
    recorder.stopCamera();
    if (recorder.takes.length > 0) {
      onSaveDraft(recorder.takes.map(t => t.blob), notes);
    } else {
      onSaveDraft([], notes);
    }
  };

  const handleClose = () => {
    recorder.resetAll();
    onClose();
  };

  switch (step) {
    case 'viewfinder':
      return (
        <>
          <VideoReady
            prompt={prompt}
            videoRef={recorder.videoRef}
            canvasRef={recorder.canvasRef}
            isReady={recorder.isReady}
            error={recorder.error}
            facingMode={recorder.facingMode}
            isRecording={recorder.isRecording}
            isPaused={recorder.isPaused}
            duration={recorder.duration}
            onStartCamera={() => { recorder.requestOrientationPermission(); recorder.startCamera(); }}
            onFlip={recorder.flipCamera}
            onRecord={handleRecord}
            onStop={handleStop}
            onPause={recorder.pauseRecording}
            onResume={recorder.resumeRecording}
            onClose={handleClose}
          />
          {showCountdown && <RecordingCountdown onComplete={handleCountdownComplete} />}
        </>
      );

    case 'preview':
      return (
        <VideoPreview
          taskName={taskName}
          videoUrl={recorder.videoUrl}
          duration={recorder.duration}
          isLandscape={isLandscape}
          onRetake={handleRetake}
          onUseVideo={handleUseVideo}
          onClose={handleClose}
        />
      );

    case 'review':
      return (
        <VideoReview
          taskName={taskName}
          takes={recorder.takes}
          onDeleteTake={handleDeleteTake}
          onRecordAnother={handleRecordAnother}
          onContinue={() => setStep('notes')}
          onClose={handleClose}
        />
      );

    case 'notes':
      return (
        <VideoNotes
          taskName={taskName}
          takes={recorder.takes}
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onBack={() => setStep('review')}
          onClose={handleClose}
          initialNotes={initialDraft?.notes || initialNotes}
        />
      );

    default:
      return null;
  }
};
